/**
 * Collections Store
 *
 * Manages lightweight named containers for ad hoc item grouping.
 * Supabase-only persistence (no localStorage).
 *
 * INVARIANT: Collections must never appear in session, pool, or ranking contexts.
 * Use promoteToItemPool() to explicitly create an Item Pool from a Collection —
 * the Collection itself is never modified by promotion.
 */

import { create } from 'zustand';
import type {
  Collection,
  CollectionRow,
  CollectionMembership,
  CollectionMembershipRow,
  CollectionMembershipSource,
  CollectionSighting,
  CollectionSightingRow,
  CollectionView,
  CollectionViewRow,
} from '../../types/prioritization/collections';
import {
  collectionRowToDomain,
  membershipRowToDomain,
  sightingRowToDomain,
  viewRowToDomain,
} from '../../types/prioritization/collections';
import {
  generateUUID,
  getCurrentWorkspaceId,
  getSafeSupabaseClient,
  createInitialSyncState,
  type SyncState,
} from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';
import { useSavedReportsStore } from './savedReportsStore';
import { useItemsStore, recordItemFieldChange } from './itemsStore';
import { useEnterpriseIdentityStore } from './enterpriseIdentityStore';
import {
  resolveSmartMembership,
  computeDelta,
  findUnsightedMatches,
  type SmartMembershipResult,
  type DeltaResult,
} from '../../lib/collections/smartCollectionMembership';
import { getSmartCollectionFieldValue } from '../../lib/collections/smartCollectionFields';
import type { QueryNode } from '../../lib/pql/types';
import type { Item } from '../../types/prioritization/lifecycle';

const COLLECTIONS_TABLE = 'collections';
const MEMBERSHIPS_TABLE = 'collection_memberships';
const SIGHTINGS_TABLE = 'collection_sightings';
const VIEWS_TABLE = 'collection_views';

interface CollectionsState {
  collections: Collection[];
  memberships: CollectionMembership[];
  sightings: CollectionSighting[];
  views: CollectionView[];
  syncState: SyncState;

  fetchFromSupabase: () => Promise<void>;

  createCollection: (name: string, description?: string, ownerId?: string) => Promise<Collection | null>;
  updateCollection: (id: string, updates: { name?: string; description?: string }) => Promise<void>;
  deleteCollection: (id: string) => Promise<void>;

  addItemToCollection: (collectionId: string, itemId: string, actorId?: string) => Promise<boolean>;
  removeItemFromCollection: (collectionId: string, itemId: string) => Promise<void>;
  reorderItems: (collectionId: string, orderedItemIds: string[]) => Promise<boolean>;

  /** Creates a new Item Pool from this Collection's current (resolved) membership. Collection is unaffected. */
  promoteToItemPool: (collectionId: string, trackId: string, actorId: string) => Promise<string | null>;

  /**
   * Binds a PQL rule to a collection, making it "smart" (PRD §4.2). Creates a
   * saved_reports row to hold the query — a COPY, which then diverges from
   * whatever the person was looking at. `sourceReportId` records the saved
   * query it was copied from, when there was one (migration 725), so the app
   * can say the rule will not follow edits to its origin.
   */
  bindRule: (collectionId: string, queryText: string, queryAst: QueryNode | null, actorId: string, sourceReportId?: string | null) => Promise<boolean>;
  /** Removes a collection's rule — it reverts to static (explicit-rows-only) behavior. The saved query itself is left intact. */
  unbindRule: (collectionId: string) => Promise<void>;

  /** Explicitly include an item a smart collection's rule doesn't (currently) match. */
  pinItem: (collectionId: string, itemId: string, actorId?: string) => Promise<boolean>;
  /** Explicitly remove an item a smart collection's rule matches. */
  excludeItem: (collectionId: string, itemId: string, actorId?: string) => Promise<boolean>;
  /** Clears a pin/exclude override — the item reverts to pure rule evaluation. */
  clearOverride: (collectionId: string, itemId: string) => Promise<void>;

  /** Records that the current user has looked at this collection now — resets its delta badge. */
  markCollectionViewed: (collectionId: string) => Promise<void>;
  /** Upserts sighting rows for any currently-matching item not yet recorded (opportunistic, no background job — PRD §4.2). */
  syncSightings: (collectionId: string, currentMatchIds: string[]) => Promise<void>;

  getCollectionsForItem: (itemId: string) => Collection[];
  getItemIdsInCollection: (collectionId: string) => string[];
  getOrderedItemIdsForCollection: (collectionId: string) => string[];
  /** Live membership for ANY collection (static or smart) — the one function UI should call to render a collection's items. */
  getResolvedMembership: (collectionId: string, items: Item[]) => SmartMembershipResult;
  /** "N new since you last looked", derived from sightings + the current user's last view. Non-authoritative (delta badge only). */
  getDelta: (collectionId: string, items: Item[]) => DeltaResult;
}

export const useCollectionsStore = create<CollectionsState>((set, get) => ({
  collections: [],
  memberships: [],
  sightings: [],
  views: [],
  syncState: createInitialSyncState(),

  fetchFromSupabase: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set({ syncState: { ...get().syncState, isLoading: true } });

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const [collectionsRes, membershipsRes, sightingsRes, viewsRes] = await Promise.all([
        supabase
          .from(COLLECTIONS_TABLE)
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false }),
        supabase
          .from(MEMBERSHIPS_TABLE)
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('sort_order', { ascending: true }),
        supabase
          .from(SIGHTINGS_TABLE)
          .select('*')
          .eq('workspace_id', workspaceId),
        // collection_views RLS scopes rows to the current user already — no
        // extra filter needed, and other users' rows wouldn't be readable anyway.
        user ? supabase.from(VIEWS_TABLE).select('*').eq('user_id', user.id) : Promise.resolve({ data: [], error: null }),
      ]);

      if (collectionsRes.error) {
        console.error('[CollectionsStore] Failed to fetch collections:', collectionsRes.error.message);
      }
      if (membershipsRes.error) {
        console.error('[CollectionsStore] Failed to fetch memberships:', membershipsRes.error.message);
      }
      if (sightingsRes.error) {
        console.error('[CollectionsStore] Failed to fetch sightings (non-blocking — delta badges only):', sightingsRes.error.message);
      }
      if (viewsRes.error) {
        console.error('[CollectionsStore] Failed to fetch views (non-blocking — delta badges only):', viewsRes.error.message);
      }

      set({
        collections: ((collectionsRes.data as CollectionRow[] | null) ?? []).map(collectionRowToDomain),
        memberships: ((membershipsRes.data as CollectionMembershipRow[] | null) ?? []).map(membershipRowToDomain),
        sightings: ((sightingsRes.data as CollectionSightingRow[] | null) ?? []).map(sightingRowToDomain),
        views: ((viewsRes.data as CollectionViewRow[] | null) ?? []).map(viewRowToDomain),
        syncState: { ...get().syncState, isLoading: false, lastSyncedAt: new Date() },
      });
    } catch (error) {
      console.error('[CollectionsStore] fetchFromSupabase failed:', error);
      set({ syncState: { ...get().syncState, isLoading: false } });
    }
  },

  createCollection: async (name, description, ownerId) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return null;

    // Resolve owner: use provided ownerId, fall back to auth user
    let resolvedOwnerId = ownerId;
    if (!resolvedOwnerId) {
      const { data: { user } } = await supabase.auth.getUser();
      resolvedOwnerId = user?.id;
    }
    if (!resolvedOwnerId) return null;

    const id = generateUUID();
    const now = new Date();
    const collection: Collection = {
      id,
      workspaceId,
      ownerId: resolvedOwnerId,
      name,
      description,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic update
    set((state) => ({ collections: [collection, ...state.collections] }));

    const { error } = await supabase.from(COLLECTIONS_TABLE).insert({
      id,
      workspace_id: workspaceId,
      owner_id: resolvedOwnerId,
      name,
      description: description ?? null,
    });

    if (error) {
      console.error('[CollectionsStore] Failed to create collection:', error.message);
      set((state) => ({ collections: state.collections.filter((c) => c.id !== id) }));
      return null;
    }

    return collection;
  },

  updateCollection: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set((state) => ({
      collections: state.collections.map((c) =>
        c.id === id ? { ...c, ...updates, updatedAt: new Date() } : c
      ),
    }));

    const dbUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.description !== undefined) dbUpdates.description = updates.description;

    const { error } = await supabase.from(COLLECTIONS_TABLE).update(dbUpdates).eq('id', id);
    if (error) {
      console.error('[CollectionsStore] Failed to update collection:', error.message);
      dispatchStoreError('Collections', 'update collection', error.message);
    }
  },

  deleteCollection: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const prev = get().collections;
    const prevMemberships = get().memberships;

    set((state) => ({
      collections: state.collections.filter((c) => c.id !== id),
      memberships: state.memberships.filter((m) => m.collectionId !== id),
    }));

    const { error } = await supabase.from(COLLECTIONS_TABLE).delete().eq('id', id);
    if (error) {
      console.error('[CollectionsStore] Failed to delete collection:', error.message);
      set({ collections: prev, memberships: prevMemberships });
    }
  },

  addItemToCollection: async (collectionId, itemId, actorId) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase) return false;

    // Idempotent: already in collection
    if (get().memberships.some((m) => m.collectionId === collectionId && m.itemId === itemId)) {
      return true;
    }

    // Resolve actor
    let resolvedActorId = actorId;
    if (!resolvedActorId) {
      const { data: { user } } = await supabase.auth.getUser();
      resolvedActorId = user?.id;
    }
    if (!resolvedActorId) return false;

    // Get current max sort_order for this collection
    const existingInCollection = get().memberships
      .filter((m) => m.collectionId === collectionId)
      .sort((a, b) => b.sortOrder - a.sortOrder);
    const nextOrder = existingInCollection.length > 0 ? existingInCollection[0].sortOrder + 1 : 0;

    const id = generateUUID();
    const membership: CollectionMembership = {
      id,
      collectionId,
      itemId,
      workspaceId: workspaceId ?? undefined,
      sortOrder: nextOrder,
      membershipSource: 'manual',
      addedBy: resolvedActorId,
      addedAt: new Date(),
    };

    set((state) => ({ memberships: [...state.memberships, membership] }));

    const { error } = await supabase.from(MEMBERSHIPS_TABLE).insert({
      id,
      collection_id: collectionId,
      item_id: itemId,
      workspace_id: workspaceId,
      sort_order: nextOrder,
      added_by: resolvedActorId,
    });

    if (error) {
      // 23505 = unique violation — already a member, treat as success
      if (error.code === '23505') return true;
      console.error('[CollectionsStore] Failed to add item to collection:', error.message);
      set((state) => ({ memberships: state.memberships.filter((m) => m.id !== id) }));
      return false;
    }

    recordItemFieldChange(itemId, 'collection', null, get().collections.find((c) => c.id === collectionId)?.name ?? collectionId);
    return true;
  },

  removeItemFromCollection: async (collectionId, itemId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const membership = get().memberships.find((m) => m.collectionId === collectionId && m.itemId === itemId);
    if (!membership) return;

    set((state) => ({ memberships: state.memberships.filter((m) => m.id !== membership.id) }));

    const { error } = await supabase.from(MEMBERSHIPS_TABLE).delete().eq('id', membership.id);
    if (error) {
      console.error('[CollectionsStore] Failed to remove item from collection:', error.message);
      get().fetchFromSupabase();
      return;
    }
    recordItemFieldChange(itemId, 'collection', get().collections.find((c) => c.id === collectionId)?.name ?? collectionId, null);
  },

  reorderItems: async (collectionId, orderedItemIds) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    // Optimistic update
    set((state) => ({
      memberships: state.memberships.map((m) => {
        if (m.collectionId !== collectionId) return m;
        const idx = orderedItemIds.indexOf(m.itemId);
        return idx >= 0 ? { ...m, sortOrder: idx } : m;
      }),
    }));

    // Batch update sort_order for all items in the new order
    const updates = orderedItemIds.map((itemId, index) =>
      supabase
        .from(MEMBERSHIPS_TABLE)
        .update({ sort_order: index })
        .eq('collection_id', collectionId)
        .eq('item_id', itemId)
    );

    const results = await Promise.all(updates);
    const anyError = results.some((r) => r.error);
    if (anyError) {
      console.error('[CollectionsStore] Reorder failed — refetching');
      await get().fetchFromSupabase();
      return false;
    }

    return true;
  },

  /**
   * Promote a Collection to an Item Pool on the specified track.
   * Creates a new item_pools row + item_pool_memberships rows.
   * The Collection itself is NOT modified.
   *
   * INVARIANT: Never query collection_memberships in session readiness checks.
   * This is the ONLY path from Collection → Item Pool and it is always explicit.
   */
  promoteToItemPool: async (collectionId, trackId, actorId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    const collection = get().collections.find((c) => c.id === collectionId);
    if (!collection) return null;

    // Resolved membership, NOT raw collection_memberships rows — for a Smart
    // Collection, rule matches are never persisted as membership rows (§4.2),
    // so filtering `get().memberships` here would silently promote only the
    // pinned items and miss every rule-matched item. This is the exact
    // dual-membership-truth bug class the PRD calls out (see migration 611's
    // history in the PRD's §1.5) — resolve once, through the one function
    // that knows how to combine rule + pins + excludes.
    const { itemIds } = get().getResolvedMembership(collectionId, useItemsStore.getState().items);

    if (itemIds.length === 0) {
      console.warn('[CollectionsStore] Cannot promote: collection is empty');
      return null;
    }

    const poolId = generateUUID();
    const now = new Date().toISOString();
    const provenanceDescription = `Created from collection: ${collection.name} on ${new Date().toLocaleDateString()} by ${actorId}`;

    // Create the item pool
    const { error: poolError } = await supabase.from('item_pools').insert({
      id: poolId,
      name: `${collection.name} (promoted)`,
      description: provenanceDescription,
      track_id: trackId,
      workspace_id: collection.workspaceId,
      status: 'draft',
      owner_id: actorId,
      created_at: now,
      updated_at: now,
      priority_ids: itemIds,
    });

    if (poolError) {
      console.error('[CollectionsStore] Promote to Item Pool failed:', poolError.message);
      return null;
    }

    // Insert item_pool_memberships rows. Rank order: for a static collection
    // this follows the collection's own sort_order; for a Smart Collection
    // there's no user-defined order (rule-derived membership has none), so
    // itemIds' resolution order is used as-is.
    // item_pool_memberships has no workspace_id column, and rank is 1-based
    // everywhere else (see itemPoolMembershipSync) — 0 here worked only because
    // rank has no check, but keep the convention consistent.
    const membershipRows = itemIds.map((itemId, index) => ({
      item_pool_id: poolId,
      item_id: itemId,
      rank: index + 1,
      added_at: now,
      added_by: actorId,
      item_source: 'collection_promotion' as const,
    }));

    const { error: membError } = await supabase.from('item_pool_memberships').insert(membershipRows);
    if (membError) {
      console.warn('[CollectionsStore] item_pool_memberships insert failed (non-blocking):', membError.message);
    }

    console.log(`[CollectionsStore] Promoted collection "${collection.name}" to item pool ${poolId} on track ${trackId}`);
    return poolId;
  },

  getCollectionsForItem: (itemId) => {
    const { collections, memberships } = get();
    const collectionIds = new Set(
      memberships.filter((m) => m.itemId === itemId).map((m) => m.collectionId)
    );
    return collections.filter((c) => collectionIds.has(c.id));
  },

  getItemIdsInCollection: (collectionId) => {
    return get()
      .memberships.filter((m) => m.collectionId === collectionId)
      .map((m) => m.itemId);
  },

  getOrderedItemIdsForCollection: (collectionId) => {
    return get()
      .memberships.filter((m) => m.collectionId === collectionId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((m) => m.itemId);
  },

  // ── Smart Collections (migration 637, PRD §4.2) ────────────────────────────

  bindRule: async (collectionId, queryText, queryAst, actorId, sourceReportId) => {
    const supabase = getSafeSupabaseClient();
    const collection = get().collections.find((c) => c.id === collectionId);
    if (!supabase || !collection) return false;

    // Awaited, not fire-and-forget: collections.rule_query_id is a FK to
    // saved_reports, so the rule row has to be committed before the collection
    // can point at it.
    const { report, error: reportError } = await useSavedReportsStore.getState().createReportPersisted({
      name: `${collection.name} (Smart Collection rule)`,
      description: `Auto-created rule query backing the "${collection.name}" Smart Collection.`,
      filterGroups: [],
      queryText,
      queryAst,
      sourceReportId: sourceReportId ?? null,
      sort: [],
      groupBy: null,
      visibleColumns: [],
      activeChart: 'category',
    });
    if (!report) {
      console.error('[CollectionsStore] bindRule could not save the rule query:', reportError);
      return false;
    }
    void actorId; // reserved for a future provenance column on saved_reports

    const prev = collection.ruleQueryId;
    set((state) => ({
      collections: state.collections.map((c) =>
        c.id === collectionId ? { ...c, ruleQueryId: report.id, updatedAt: new Date() } : c
      ),
    }));

    const { error } = await supabase
      .from(COLLECTIONS_TABLE)
      .update({ rule_query_id: report.id, updated_at: new Date().toISOString() })
      .eq('id', collectionId);

    if (error) {
      console.error('[CollectionsStore] bindRule failed:', error.message);
      set((state) => ({
        collections: state.collections.map((c) =>
          c.id === collectionId ? { ...c, ruleQueryId: prev } : c
        ),
      }));
      // Drop the rule query too: unbound, it is an orphan in the library that
      // says it backs a collection it does not back. The local revert above
      // runs first so the delete is not refused by its own dependents check.
      useSavedReportsStore.getState().deleteReport(report.id);
      return false;
    }
    return true;
  },

  unbindRule: async (collectionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const prev = get().collections.find((c) => c.id === collectionId)?.ruleQueryId;
    set((state) => ({
      collections: state.collections.map((c) =>
        c.id === collectionId ? { ...c, ruleQueryId: undefined, updatedAt: new Date() } : c
      ),
    }));

    const { error } = await supabase
      .from(COLLECTIONS_TABLE)
      .update({ rule_query_id: null, updated_at: new Date().toISOString() })
      .eq('id', collectionId);

    if (error) {
      console.error('[CollectionsStore] unbindRule failed:', error.message);
      set((state) => ({
        collections: state.collections.map((c) =>
          c.id === collectionId ? { ...c, ruleQueryId: prev } : c
        ),
      }));
    }
  },

  pinItem: async (collectionId, itemId, actorId) => {
    return setMembershipOverride(set, get, collectionId, itemId, 'pinned', actorId);
  },

  excludeItem: async (collectionId, itemId, actorId) => {
    return setMembershipOverride(set, get, collectionId, itemId, 'excluded', actorId);
  },

  clearOverride: async (collectionId, itemId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const existing = get().memberships.find((m) => m.collectionId === collectionId && m.itemId === itemId);
    if (!existing) return;

    set((state) => ({ memberships: state.memberships.filter((m) => m.id !== existing.id) }));

    const { error } = await supabase.from(MEMBERSHIPS_TABLE).delete().eq('id', existing.id);
    if (error) {
      console.error('[CollectionsStore] clearOverride failed:', error.message);
      set((state) => ({ memberships: [...state.memberships, existing] }));
    }
  },

  markCollectionViewed: async (collectionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const now = new Date();
    set((state) => ({
      views: [
        ...state.views.filter((v) => !(v.collectionId === collectionId && v.userId === user.id)),
        { collectionId, userId: user.id, lastViewedAt: now },
      ],
    }));

    const { error } = await supabase
      .from(VIEWS_TABLE)
      .upsert(
        { collection_id: collectionId, user_id: user.id, last_viewed_at: now.toISOString() },
        { onConflict: 'collection_id,user_id' },
      );
    if (error) {
      console.error('[CollectionsStore] markCollectionViewed failed (non-blocking):', error.message);
    }
  },

  syncSightings: async (collectionId, currentMatchIds) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    const existingIds = get()
      .sightings.filter((s) => s.collectionId === collectionId)
      .map((s) => s.itemId);
    const unsighted = findUnsightedMatches(currentMatchIds, existingIds);
    if (unsighted.length === 0) return;

    const now = new Date();
    set((state) => ({
      sightings: [
        ...state.sightings,
        ...unsighted.map((itemId) => ({ collectionId, itemId, workspaceId, firstMatchedAt: now })),
      ],
    }));

    const { error } = await supabase.from(SIGHTINGS_TABLE).insert(
      unsighted.map((itemId) => ({
        collection_id: collectionId,
        item_id: itemId,
        workspace_id: workspaceId,
        first_matched_at: now.toISOString(),
      })),
    );
    if (error) {
      // 23505 = unique violation (another tab/session sighted it first) — harmless
      if (error.code !== '23505') {
        console.error('[CollectionsStore] syncSightings failed (non-blocking — delta badge only):', error.message);
      }
    }
  },

  getResolvedMembership: (collectionId, items) => {
    const state = get();
    const collection = state.collections.find((c) => c.id === collectionId);
    if (!collection) return { itemIds: [], ruleMatchIds: new Set(), isSmart: false };

    let ruleAst: QueryNode | null | undefined;
    if (collection.ruleQueryId) {
      const report = useSavedReportsStore.getState().getReportById(collection.ruleQueryId);
      // queryAst may legitimately be `null` (a bound rule that matches everything);
      // it is `undefined` only when the report predates PQL persistence (migration
      // 637) — fall back to static behavior rather than silently matching everything.
      ruleAst = report ? report.queryAst : undefined;
    }

    return resolveSmartMembership({
      items,
      getId: (item) => item.id,
      getFieldValue: getSmartCollectionFieldValue,
      ruleAst,
      explicitMemberships: state.memberships
        .filter((m) => m.collectionId === collectionId)
        .map((m) => ({ itemId: m.itemId, source: m.membershipSource })),
    });
  },

  getDelta: (collectionId, items) => {
    const state = get();
    const { itemIds } = get().getResolvedMembership(collectionId, items);
    const currentUserId = useEnterpriseIdentityStore.getState().currentUserProfile?.id;
    const lastViewedAt =
      state.views.find((v) => v.collectionId === collectionId && v.userId === currentUserId)?.lastViewedAt ?? null;
    const sightings = state.sightings
      .filter((s) => s.collectionId === collectionId)
      .map((s) => ({ itemId: s.itemId, firstMatchedAt: s.firstMatchedAt }));
    return computeDelta(itemIds, sightings, lastViewedAt);
  },
}));

/**
 * Shared by pinItem/excludeItem: upsert-by-(collectionId,itemId) since the
 * table has a UNIQUE(collection_id, item_id) constraint (migration 316) — an
 * existing row (of any source) is updated in place rather than duplicated.
 */
async function setMembershipOverride(
  set: (fn: (state: CollectionsState) => Partial<CollectionsState>) => void,
  get: () => CollectionsState,
  collectionId: string,
  itemId: string,
  source: CollectionMembershipSource,
  actorId?: string,
): Promise<boolean> {
  const supabase = getSafeSupabaseClient();
  const workspaceId = getCurrentWorkspaceId();
  if (!supabase) return false;

  const existing = get().memberships.find((m) => m.collectionId === collectionId && m.itemId === itemId);

  if (existing) {
    set((state) => ({
      memberships: state.memberships.map((m) => (m.id === existing.id ? { ...m, membershipSource: source } : m)),
    }));
    const { error } = await supabase
      .from(MEMBERSHIPS_TABLE)
      .update({ membership_source: source })
      .eq('id', existing.id);
    if (error) {
      console.error('[CollectionsStore] setMembershipOverride (update) failed:', error.message);
      set((state) => ({
        memberships: state.memberships.map((m) => (m.id === existing.id ? existing : m)),
      }));
      return false;
    }
    return true;
  }

  const id = generateUUID();
  const membership: CollectionMembership = {
    id,
    collectionId,
    itemId,
    workspaceId: workspaceId ?? undefined,
    sortOrder: 0,
    membershipSource: source,
    addedBy: actorId,
    addedAt: new Date(),
  };
  set((state) => ({ memberships: [...state.memberships, membership] }));

  const { error } = await supabase.from(MEMBERSHIPS_TABLE).insert({
    id,
    collection_id: collectionId,
    item_id: itemId,
    workspace_id: workspaceId,
    sort_order: 0,
    membership_source: source,
    added_by: actorId,
  });
  if (error) {
    if (error.code === '23505') return true; // race with another insert — treat as success
    console.error('[CollectionsStore] setMembershipOverride (insert) failed:', error.message);
    set((state) => ({ memberships: state.memberships.filter((m) => m.id !== id) }));
    return false;
  }
  return true;
}
